import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Badge,
    Button,
    Col,
    Form,
    Modal,
    Nav,
    Row,
    Spinner,
    Tab,
} from 'react-bootstrap';
import { useSelector } from 'react-redux';
import {
    FiBell,
    FiEye,
    FiLayers,
    FiMail,
    FiMessageSquare,
    FiPlay,
    FiRefreshCw,
    FiTarget,
    FiUploadCloud,
} from 'react-icons/fi';
import {
    backupStandardCheckObservations,
    sendObservationTriggeredBulkEmail,
} from '../../services/api';
import { formatRequestError } from '../../utils/errorHandler';
import {
    canRunStandardScheduledForecastImport,
    getAdminRoleFromToken,
} from '../../utils/ecmwfExcelImport';
import { useTranslation } from '../../hooks/useTranslation';
import { useLongRunningProgress, getLongWaitMessage } from '../../hooks/useLongRunningProgress';
import LongRunningOperationBanner from '../../components/Admin/LongRunningOperationBanner';
import './AdminTripsForecastBackupCheck.css';

const PREFIX = 'pages.adminTripsObservationBackupCheck';

const MAIN_TAB_KEYS = {
    BACKUP_TOOLS: 'backupTools',
    NOTIFICATION: 'notification',
};

const FAMILY_KEYS = {
    STANDARD: 'standard',
    SPECIFIC: 'specific',
};

const TAB_KEYS = {
    PARAMETERS: 'parameters',
    BULK: 'bulk',
    STANDARD_IMPORT_EXCEL: 'standardImportExcel',
    BULK_EMAIL: 'bulkEmail',
    BULK_SMS: 'bulkSms',
    STANDARD_BULK_EMAIL: 'standardBulkEmail',
    STANDARD_BULK_NOT_TRIGGERED_EMAIL: 'standardBulkNotTriggeredEmail',
    STANDARD_BULK_SMS: 'standardBulkSms',
};

const BACKUP_TAB_KEYS = new Set([TAB_KEYS.PARAMETERS, TAB_KEYS.BULK]);
const NOTIFY_EMAIL_TAB_KEYS = new Set([
    TAB_KEYS.BULK_EMAIL,
    TAB_KEYS.STANDARD_BULK_EMAIL,
    TAB_KEYS.STANDARD_BULK_NOT_TRIGGERED_EMAIL,
]);
const NOTIFY_SMS_TAB_KEYS = new Set([TAB_KEYS.BULK_SMS, TAB_KEYS.STANDARD_BULK_SMS]);
const STANDARD_NOTIFY_PLACEHOLDER_KEYS = new Set([
    TAB_KEYS.STANDARD_BULK_EMAIL,
    TAB_KEYS.STANDARD_BULK_NOT_TRIGGERED_EMAIL,
    TAB_KEYS.STANDARD_BULK_SMS,
]);

const OBSERVATION_DATA_SOURCES = ['METEOFRANCE', 'METEOSTAT'];
const DEFAULT_DATA_SOURCE = 'METEOFRANCE';
const BACKUP_ENDPOINT = '/api/standard/trips/backup/check-observations';
const SPECIFIC_EMAIL_ENDPOINT = '/api/trips/observation-triggered/bulk-send-email';

const emptyTabState = () => ({
    tripDay: '',
    policyCode: '',
    dataSource: DEFAULT_DATA_SOURCE,
    loading: false,
    error: null,
    result: null,
});

const formatProcessingTime = (ms) => {
    if (ms == null || ms === '') return null;
    const n = Number(ms);
    if (Number.isNaN(n)) return String(ms);
    return n >= 1000 ? `${(n / 1000).toFixed(1)} s` : `${Math.round(n)} ms`;
};

const AdminTripsObservationBackupCheck = () => {
    const { t } = useTranslation();
    const accessToken = useSelector((state) => state.adminAuth?.accessToken);
    const role = getAdminRoleFromToken(accessToken || localStorage.getItem('admin_access_token'));
    const canRunBackup = canRunStandardScheduledForecastImport(role);

    const [mainTab, setMainTab] = useState(MAIN_TAB_KEYS.BACKUP_TOOLS);
    const [family, setFamily] = useState(FAMILY_KEYS.STANDARD);
    const [backupSubTab, setBackupSubTab] = useState(TAB_KEYS.BULK);
    const [notifySubTab, setNotifySubTab] = useState(TAB_KEYS.BULK_EMAIL);
    const [notifyStandardSubTab, setNotifyStandardSubTab] = useState(TAB_KEYS.STANDARD_BULK_EMAIL);
    const [showSpecificEmailConfirm, setShowSpecificEmailConfirm] = useState(false);

    const activeTab = mainTab === MAIN_TAB_KEYS.NOTIFICATION
        ? (family === FAMILY_KEYS.STANDARD ? notifyStandardSubTab : notifySubTab)
        : backupSubTab;

    const [tabState, setTabState] = useState({
        [TAB_KEYS.PARAMETERS]: emptyTabState(),
        [TAB_KEYS.BULK]: emptyTabState(),
        [TAB_KEYS.BULK_EMAIL]: emptyTabState(),
        [TAB_KEYS.BULK_SMS]: emptyTabState(),
        [TAB_KEYS.STANDARD_BULK_EMAIL]: emptyTabState(),
        [TAB_KEYS.STANDARD_BULK_NOT_TRIGGERED_EMAIL]: emptyTabState(),
        [TAB_KEYS.STANDARD_BULK_SMS]: emptyTabState(),
    });

    const current = tabState[activeTab] ?? emptyTabState();
    const isAnyTabLoading = useMemo(
        () => Object.values(tabState).some((tab) => tab?.loading),
        [tabState],
    );

    const progressSteps = useMemo(() => {
        if (NOTIFY_EMAIL_TAB_KEYS.has(activeTab)) {
            return [
                t(`${PREFIX}.progress.email.step1`),
                t(`${PREFIX}.progress.email.step2`),
                t(`${PREFIX}.progress.email.step3`),
            ];
        }
        if (NOTIFY_SMS_TAB_KEYS.has(activeTab)) {
            return [
                t(`${PREFIX}.progress.sms.step1`),
                t(`${PREFIX}.progress.sms.step2`),
                t(`${PREFIX}.progress.sms.step3`),
            ];
        }
        return [
            t(`${PREFIX}.progress.step1`),
            t(`${PREFIX}.progress.step2`),
            t(`${PREFIX}.progress.step3`),
        ];
    }, [activeTab, t]);

    const progressStepIntervalMs = NOTIFY_EMAIL_TAB_KEYS.has(activeTab)
        || NOTIFY_SMS_TAB_KEYS.has(activeTab) ? 5000 : 8000;

    const { currentStep, elapsedSeconds } = useLongRunningProgress(
        current.loading,
        progressSteps,
        progressStepIntervalMs,
    );

    useEffect(() => {
        if (!isAnyTabLoading) return undefined;

        const handleBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = '';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isAnyTabLoading]);

    const updateCurrentTab = (patch) => {
        setTabState((prev) => ({
            ...prev,
            [activeTab]: {
                ...(prev[activeTab] ?? emptyTabState()),
                ...patch,
            },
        }));
    };

    const handleReset = () => {
        setShowSpecificEmailConfirm(false);
        setTabState((prev) => ({
            ...prev,
            [activeTab]: emptyTabState(),
        }));
    };

    const executeAction = async (forcedTabKey) => {
        const tabKey = forcedTabKey || activeTab;
        const tabSnapshot = tabState[tabKey] ?? emptyTabState();

        if (BACKUP_TAB_KEYS.has(tabKey)) {
            if (!canRunBackup) {
                setTabState((prev) => ({
                    ...prev,
                    [tabKey]: {
                        ...(prev[tabKey] ?? emptyTabState()),
                        error: t(`${PREFIX}.standard.errors.forbidden`),
                    },
                }));
                return;
            }

            const tripDay = tabSnapshot.tripDay.trim();
            if (!tripDay) {
                setTabState((prev) => ({
                    ...prev,
                    [tabKey]: {
                        ...(prev[tabKey] ?? emptyTabState()),
                        error: t(`${PREFIX}.tripDayRequired`),
                    },
                }));
                return;
            }

            setTabState((prev) => ({
                ...prev,
                [tabKey]: { ...(prev[tabKey] ?? emptyTabState()), loading: true, error: null, result: null },
            }));

            try {
                const params = {
                    data_source: tabSnapshot.dataSource || DEFAULT_DATA_SOURCE,
                    trip_day: tripDay,
                };
                if (tabKey === TAB_KEYS.PARAMETERS && tabSnapshot.policyCode.trim()) {
                    params.policy_code = tabSnapshot.policyCode.trim();
                }
                const response = await backupStandardCheckObservations(params);
                setTabState((prev) => ({
                    ...prev,
                    [tabKey]: { ...(prev[tabKey] ?? emptyTabState()), result: response, loading: false },
                }));
            } catch (requestError) {
                setTabState((prev) => ({
                    ...prev,
                    [tabKey]: {
                        ...(prev[tabKey] ?? emptyTabState()),
                        error: parseActionError(requestError, tabKey, t),
                        loading: false,
                    },
                }));
            }
            return;
        }

        if (tabKey === TAB_KEYS.BULK_EMAIL) {
            setShowSpecificEmailConfirm(false);
            setTabState((prev) => ({
                ...prev,
                [tabKey]: { ...(prev[tabKey] ?? emptyTabState()), loading: true, error: null, result: null },
            }));

            try {
                const response = await sendObservationTriggeredBulkEmail();
                setTabState((prev) => ({
                    ...prev,
                    [tabKey]: { ...(prev[tabKey] ?? emptyTabState()), result: response, loading: false },
                }));
            } catch (requestError) {
                setTabState((prev) => ({
                    ...prev,
                    [tabKey]: {
                        ...(prev[tabKey] ?? emptyTabState()),
                        error: parseActionError(requestError, tabKey, t),
                        loading: false,
                    },
                }));
            }
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (activeTab === TAB_KEYS.BULK_EMAIL) {
            setShowSpecificEmailConfirm(true);
            return;
        }
        executeAction();
    };

    const renderLoadingBanner = () => {
        const longWaitNote = getLongWaitMessage(elapsedSeconds, {
            stillNormal: t(`${PREFIX}.progress.stillNormal`),
            long: t(`${PREFIX}.progress.longWait`),
            twoMinutes: t(`${PREFIX}.progress.twoMinutesWait`),
            veryLong: t(`${PREFIX}.progress.veryLongWait`),
            overFourMinutes: t(`${PREFIX}.progress.overFourMinutesWait`),
        });

        return (
            <LongRunningOperationBanner
                show={current.loading}
                sticky
                title={t(`${PREFIX}.progress.title`)}
                expectedDuration={t(`${PREFIX}.progress.expectedDuration`)}
                message={currentStep}
                elapsedSeconds={elapsedSeconds}
                elapsedLabel={t(`${PREFIX}.progress.elapsed`)}
                longWaitNote={longWaitNote}
                hint={t(`${PREFIX}.progress.hint`)}
            />
        );
    };

    const renderResult = () => {
        if (current.error) {
            return (
                <Alert variant="danger" dismissible onClose={() => updateCurrentTab({ error: null })}>
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                        <span>{current.error}</span>
                        {(canRunBackup || activeTab === TAB_KEYS.BULK_EMAIL) && (
                            <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => executeAction(activeTab)}
                                disabled={current.loading}
                            >
                                <FiRefreshCw size={14} className="me-1" aria-hidden />
                                {t(`${PREFIX}.retry`)}
                            </Button>
                        )}
                    </div>
                </Alert>
            );
        }

        if (!current.result) return null;

        const summary = current.result.summary && typeof current.result.summary === 'object'
            ? current.result.summary
            : {};
        const metadata = current.result.check_observations?.metadata
            && typeof current.result.check_observations.metadata === 'object'
            ? current.result.check_observations.metadata
            : {};
        const resultStatus = current.result.status
            ?? (current.result.status_code === 200 ? 'success' : null);
        const resultVariant = resultStatus === 'success' ? 'success' : 'info';

        return (
            <Alert variant={resultVariant}>
                <div className="d-flex align-items-center gap-2 mb-2">
                    <strong>{t(`${PREFIX}.resultTitle`)}</strong>
                    {resultStatus && (
                        <Badge bg={resultStatus === 'success' ? 'success' : 'secondary'}>
                            {resultStatus}
                        </Badge>
                    )}
                </div>
                {current.result.message && <p className="mb-3">{current.result.message}</p>}
                <Row className="g-3 small">
                    {current.result.status_code != null && (
                        <Col md={4}>
                            <span className="text-muted">{t(`${PREFIX}.statusCode`)}</span>
                            <div><strong>{current.result.status_code}</strong></div>
                        </Col>
                    )}
                    {current.result.timestamp && (
                        <Col md={4}>
                            <span className="text-muted">{t(`${PREFIX}.timestamp`)}</span>
                            <div><strong>{current.result.timestamp}</strong></div>
                        </Col>
                    )}
                    {(current.result.trip_day || summary.trip_day) && (
                        <Col md={4}>
                            <span className="text-muted">{t(`${PREFIX}.tripDay`)}</span>
                            <div><strong>{current.result.trip_day || summary.trip_day}</strong></div>
                        </Col>
                    )}
                    {current.result.policy_code && (
                        <Col md={4}>
                            <span className="text-muted">{t(`${PREFIX}.policyCode`)}</span>
                            <div><strong>{current.result.policy_code}</strong></div>
                        </Col>
                    )}
                    {(current.result.data_source || summary.data_source) && (
                        <Col md={4}>
                            <span className="text-muted">{t(`${PREFIX}.standard.dataSource`)}</span>
                            <div><strong>{current.result.data_source || summary.data_source}</strong></div>
                        </Col>
                    )}
                    {summary.total_policies_processed != null && (
                        <Col md={4}>
                            <span className="text-muted">{t(`${PREFIX}.policiesProcessed`)}</span>
                            <div><strong>{summary.total_policies_processed}</strong></div>
                        </Col>
                    )}
                    {summary.policies_triggered != null && (
                        <Col md={4}>
                            <span className="text-muted">{t(`${PREFIX}.policiesTriggered`)}</span>
                            <div><strong>{summary.policies_triggered}</strong></div>
                        </Col>
                    )}
                    {summary.policies_not_triggered != null && (
                        <Col md={4}>
                            <span className="text-muted">{t(`${PREFIX}.policiesNotTriggered`)}</span>
                            <div><strong>{summary.policies_not_triggered}</strong></div>
                        </Col>
                    )}
                    {summary.policies_with_errors != null && (
                        <Col md={4}>
                            <span className="text-muted">{t(`${PREFIX}.policiesWithErrors`)}</span>
                            <div><strong>{summary.policies_with_errors}</strong></div>
                        </Col>
                    )}
                    {(summary.locations_failed != null || metadata.locations_failed != null) && (
                        <Col md={4}>
                            <span className="text-muted">{t(`${PREFIX}.locationsFailed`)}</span>
                            <div><strong>{summary.locations_failed ?? metadata.locations_failed}</strong></div>
                        </Col>
                    )}
                    {(summary.observation_hour ?? metadata.observation_hour) != null && (
                        <Col md={4}>
                            <span className="text-muted">{t(`${PREFIX}.observationHour`)}</span>
                            <div><strong>{summary.observation_hour ?? metadata.observation_hour}</strong></div>
                        </Col>
                    )}
                    {formatProcessingTime(summary.processing_time_ms ?? metadata.processing_time_ms) && (
                        <Col md={4}>
                            <span className="text-muted">{t(`${PREFIX}.processingTime`)}</span>
                            <div>
                                <strong>
                                    {formatProcessingTime(summary.processing_time_ms ?? metadata.processing_time_ms)}
                                </strong>
                            </div>
                        </Col>
                    )}
                    {metadata.timezone && (
                        <Col md={4}>
                            <span className="text-muted">{t(`${PREFIX}.timezone`)}</span>
                            <div><strong>{metadata.timezone}</strong></div>
                        </Col>
                    )}
                </Row>
            </Alert>
        );
    };

    const renderFormFields = ({ idPrefix, showPolicyCode = false }) => (
        <div className="fb-fields">
            <div className="fb-field">
                <label className="fb-field__label" htmlFor={`${idPrefix}-data-source`}>
                    {t(`${PREFIX}.standard.dataSource`)}
                </label>
                <Form.Select
                    id={`${idPrefix}-data-source`}
                    value={current.dataSource || DEFAULT_DATA_SOURCE}
                    onChange={(e) => updateCurrentTab({ dataSource: e.target.value })}
                    disabled={current.loading || !canRunBackup}
                >
                    {OBSERVATION_DATA_SOURCES.map((source) => (
                        <option key={source} value={source}>{source}</option>
                    ))}
                </Form.Select>
                <p className="fb-field__help">{t(`${PREFIX}.standard.dataSourceHint`)}</p>
            </div>
            <div className="fb-field">
                <label className="fb-field__label" htmlFor={`${idPrefix}-trip-day`}>
                    {t(`${PREFIX}.tripDay`)} *
                </label>
                <Form.Control
                    id={`${idPrefix}-trip-day`}
                    type="date"
                    value={current.tripDay}
                    onChange={(e) => updateCurrentTab({ tripDay: e.target.value })}
                    disabled={current.loading || !canRunBackup}
                    required
                />
                <p className="fb-field__help">{t(`${PREFIX}.tripDayHint`)}</p>
            </div>
            {showPolicyCode && (
                <div className="fb-field">
                    <label className="fb-field__label" htmlFor={`${idPrefix}-policy-code`}>
                        {t(`${PREFIX}.policyCode`)}
                    </label>
                    <Form.Control
                        id={`${idPrefix}-policy-code`}
                        type="text"
                        value={current.policyCode}
                        onChange={(e) => updateCurrentTab({ policyCode: e.target.value })}
                        placeholder={t(`${PREFIX}.policyCodePlaceholder`)}
                        disabled={current.loading || !canRunBackup}
                    />
                    <p className="fb-field__help">{t(`${PREFIX}.policyCodeHint`)}</p>
                </div>
            )}
        </div>
    );

    const renderParametersForm = () => (
        <div className={`fb-panel${current.loading ? ' fb-panel--busy' : ''}`}>
            <div className="fb-panel__header">
                <h3 className="fb-panel__title">
                    <FiTarget size={22} aria-hidden />
                    {t(`${PREFIX}.pageTabs.parameters`)}
                </h3>
                <p className="fb-panel__desc">{t(`${PREFIX}.standard.description`)}</p>
                <p className="fb-duration-notice" role="note">{t(`${PREFIX}.progress.expectedDuration`)}</p>
                <div className="fb-endpoint" role="note">
                    <span className="fb-endpoint__method">GET</span>
                    <code>{BACKUP_ENDPOINT}</code>
                </div>
            </div>
            <div className="fb-panel__body">
                {!canRunBackup && (
                    <Alert variant="warning" className="mb-3">
                        {t(`${PREFIX}.standard.errors.forbidden`)}
                    </Alert>
                )}
                <Form id="obs-backup-parameters-form" onSubmit={handleSubmit}>
                    {renderFormFields({ idPrefix: 'obs-backup-parameters', showPolicyCode: true })}
                </Form>
            </div>
            <div className="fb-panel__footer">
                <Button type="submit" form="obs-backup-parameters-form" className="btn fb-btn-primary" disabled={current.loading || !canRunBackup}>
                    {current.loading ? (
                        <>
                            <Spinner size="sm" animation="border" />
                            {t(`${PREFIX}.running`)}
                        </>
                    ) : (
                        <>
                            <FiPlay size={16} aria-hidden />
                            {t(`${PREFIX}.runButton`)}
                        </>
                    )}
                </Button>
                <Button type="button" className="btn fb-btn-secondary" onClick={handleReset} disabled={current.loading}>
                    <FiRefreshCw size={16} aria-hidden />
                    {t('common.reset')}
                </Button>
            </div>
        </div>
    );

    const renderBulkForm = () => (
        <div className={`fb-panel${current.loading ? ' fb-panel--busy' : ''}`}>
            <div className="fb-panel__header">
                <h3 className="fb-panel__title">
                    <FiLayers size={22} aria-hidden />
                    {t(`${PREFIX}.pageTabs.bulk`)}
                </h3>
                <p className="fb-panel__desc">{t(`${PREFIX}.standard.bulkDescription`)}</p>
                <p className="fb-duration-notice" role="note">{t(`${PREFIX}.progress.expectedDuration`)}</p>
                <div className="fb-endpoint" role="note">
                    <span className="fb-endpoint__method">GET</span>
                    <code>{BACKUP_ENDPOINT}</code>
                </div>
            </div>
            <div className="fb-panel__body">
                {!canRunBackup && (
                    <Alert variant="warning" className="mb-3">
                        {t(`${PREFIX}.standard.errors.forbidden`)}
                    </Alert>
                )}
                <div className="fb-alert-warning" role="status">
                    <FiLayers size={18} aria-hidden />
                    <span>{t(`${PREFIX}.standard.bulkInfo`)}</span>
                </div>
                <Form id="obs-backup-bulk-form" onSubmit={handleSubmit}>
                    {renderFormFields({ idPrefix: 'obs-backup-bulk' })}
                </Form>
            </div>
            <div className="fb-panel__footer">
                <Button type="submit" form="obs-backup-bulk-form" className="btn fb-btn-primary" disabled={current.loading || !canRunBackup}>
                    {current.loading ? (
                        <>
                            <Spinner size="sm" animation="border" />
                            {t(`${PREFIX}.bulk.running`)}
                        </>
                    ) : (
                        <>
                            <FiPlay size={16} aria-hidden />
                            {t(`${PREFIX}.bulk.runButton`)}
                        </>
                    )}
                </Button>
                <Button type="button" className="btn fb-btn-secondary" onClick={handleReset} disabled={current.loading}>
                    <FiRefreshCw size={16} aria-hidden />
                    {t('common.reset')}
                </Button>
            </div>
        </div>
    );

    const renderImportExcelPanel = () => (
        <div className="fb-panel fb-placeholder" role="status">
            <div className="fb-panel__header">
                <h3 className="fb-panel__title">
                    <FiUploadCloud size={22} aria-hidden />
                    {t(`${PREFIX}.pageTabs.importExcel`)}
                </h3>
                <p className="fb-panel__desc">{t(`${PREFIX}.standardImportExcel.description`)}</p>
            </div>
            <div className="fb-panel__body">
                <p className="mb-0 text-muted">{t(`${PREFIX}.standardImportExcel.notifySoon`)}</p>
            </div>
        </div>
    );

    const renderPlaceholderPanel = (title, message) => (
        <div className="fb-panel fb-placeholder" role="status">
            <h3 className="fb-panel__title mb-2">{title}</h3>
            <p className="mb-0 text-muted">{message}</p>
        </div>
    );

    const renderStandardNotifyPlaceholder = (i18nKey, title) => renderPlaceholderPanel(
        title,
        t(`${PREFIX}.${i18nKey}.notifySoon`),
    );

    const renderSpecificEmailForm = () => (
        <div className={`fb-panel${current.loading ? ' fb-panel--busy' : ''}`}>
            <div className="fb-panel__header">
                <h3 className="fb-panel__title">
                    <FiMail size={22} aria-hidden />
                    {t(`${PREFIX}.pageTabs.bulkEmail`)}
                </h3>
                <p className="fb-panel__desc">{t(`${PREFIX}.bulkEmail.description`)}</p>
                <div className="fb-endpoint" role="note">
                    <span className="fb-endpoint__method">GET</span>
                    <code>{SPECIFIC_EMAIL_ENDPOINT}</code>
                </div>
            </div>
            <div className="fb-panel__body">
                <div className="fb-alert-warning" role="alert">
                    <FiMail size={18} aria-hidden />
                    <span>{t(`${PREFIX}.bulkEmail.warning`)}</span>
                </div>
                <Form id="obs-backup-specific-email-form" onSubmit={handleSubmit} />
            </div>
            <div className="fb-panel__footer">
                <Button type="submit" form="obs-backup-specific-email-form" className="btn fb-btn-primary" disabled={current.loading}>
                    {current.loading ? (
                        <>
                            <Spinner size="sm" animation="border" />
                            {t(`${PREFIX}.bulkEmail.running`)}
                        </>
                    ) : (
                        <>
                            <FiPlay size={16} aria-hidden />
                            {t(`${PREFIX}.bulkEmail.runButton`)}
                        </>
                    )}
                </Button>
                <Button type="button" className="btn fb-btn-secondary" onClick={handleReset} disabled={current.loading}>
                    <FiRefreshCw size={16} aria-hidden />
                    {t('common.reset')}
                </Button>
            </div>
            <Modal
                show={showSpecificEmailConfirm}
                onHide={() => !current.loading && setShowSpecificEmailConfirm(false)}
                centered
                className="backup-email-confirm-modal"
            >
                <Modal.Header closeButton={!current.loading}>
                    <Modal.Title>
                        <FiMail className="me-2" aria-hidden />
                        {t(`${PREFIX}.bulkEmail.confirmTitle`)}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-2">{t(`${PREFIX}.bulkEmail.confirmMessage`)}</p>
                    <p className="text-muted mb-0">{t(`${PREFIX}.bulkEmail.confirmDescription`)}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        type="button"
                        variant="outline-secondary"
                        className="btn fb-btn-secondary"
                        onClick={() => setShowSpecificEmailConfirm(false)}
                        disabled={current.loading}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        type="button"
                        variant="primary"
                        className="btn fb-btn-primary"
                        onClick={() => executeAction(TAB_KEYS.BULK_EMAIL)}
                        disabled={current.loading}
                    >
                        {current.loading ? (
                            <>
                                <Spinner size="sm" animation="border" />
                                {t(`${PREFIX}.bulkEmail.running`)}
                            </>
                        ) : (
                            <>
                                <FiMail size={16} aria-hidden />
                                {t(`${PREFIX}.bulkEmail.confirmSend`)}
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );

    const renderBackupToolsContent = () => (
        <Tab.Container
            activeKey={family}
            onSelect={(key) => {
                if (key && !isAnyTabLoading) setFamily(key);
            }}
        >
            <Nav className="fb-segmented" role="tablist" aria-label={t(`${PREFIX}.pageTabs.backupTools`)}>
                <Nav.Item>
                    <Nav.Link eventKey={FAMILY_KEYS.STANDARD} disabled={isAnyTabLoading}>
                        {t(`${PREFIX}.pageTabs.standard`)}
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey={FAMILY_KEYS.SPECIFIC} disabled={isAnyTabLoading}>
                        {t(`${PREFIX}.pageTabs.specific`)}
                    </Nav.Link>
                </Nav.Item>
            </Nav>
            <Tab.Content>
                <Tab.Pane eventKey={FAMILY_KEYS.STANDARD}>
                    <Tab.Container
                        activeKey={backupSubTab}
                        onSelect={(key) => {
                            if (key && !isAnyTabLoading) setBackupSubTab(key);
                        }}
                    >
                        <Nav className="fb-segmented fb-segmented--sub" role="tablist" aria-label={t(`${PREFIX}.pageTabs.standard`)}>
                            <Nav.Item>
                                <Nav.Link eventKey={TAB_KEYS.PARAMETERS} disabled={isAnyTabLoading}>
                                    <FiTarget size={15} aria-hidden />
                                    {t(`${PREFIX}.pageTabs.parameters`)}
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey={TAB_KEYS.BULK} disabled={isAnyTabLoading}>
                                    <FiLayers size={15} aria-hidden />
                                    {t(`${PREFIX}.pageTabs.bulk`)}
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey={TAB_KEYS.STANDARD_IMPORT_EXCEL} disabled={isAnyTabLoading}>
                                    <FiUploadCloud size={15} aria-hidden />
                                    {t(`${PREFIX}.pageTabs.importExcel`)}
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>
                        <Tab.Content>
                            <Tab.Pane eventKey={TAB_KEYS.PARAMETERS}>
                                {renderParametersForm()}
                                {renderResult()}
                            </Tab.Pane>
                            <Tab.Pane eventKey={TAB_KEYS.BULK}>
                                {renderBulkForm()}
                                {renderResult()}
                            </Tab.Pane>
                            <Tab.Pane eventKey={TAB_KEYS.STANDARD_IMPORT_EXCEL}>
                                {renderImportExcelPanel()}
                            </Tab.Pane>
                        </Tab.Content>
                    </Tab.Container>
                </Tab.Pane>
                <Tab.Pane eventKey={FAMILY_KEYS.SPECIFIC}>
                    {renderPlaceholderPanel(
                        t(`${PREFIX}.pageTabs.specific`),
                        t(`${PREFIX}.specificPlaceholder`),
                    )}
                </Tab.Pane>
            </Tab.Content>
        </Tab.Container>
    );

    const renderNotificationContent = () => (
        <Tab.Container
            activeKey={family}
            onSelect={(key) => {
                if (key && !isAnyTabLoading) setFamily(key);
            }}
        >
            <Nav className="fb-segmented" role="tablist" aria-label={t(`${PREFIX}.pageTabs.notification`)}>
                <Nav.Item>
                    <Nav.Link eventKey={FAMILY_KEYS.STANDARD} disabled={isAnyTabLoading}>
                        {t(`${PREFIX}.pageTabs.standard`)}
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey={FAMILY_KEYS.SPECIFIC} disabled={isAnyTabLoading}>
                        {t(`${PREFIX}.pageTabs.specific`)}
                    </Nav.Link>
                </Nav.Item>
            </Nav>
            <Tab.Content>
                <Tab.Pane eventKey={FAMILY_KEYS.STANDARD}>
                    <Tab.Container
                        activeKey={notifyStandardSubTab}
                        onSelect={(key) => {
                            if (key && !isAnyTabLoading) setNotifyStandardSubTab(key);
                        }}
                    >
                        <Nav className="fb-segmented fb-segmented--sub" role="tablist" aria-label={t(`${PREFIX}.pageTabs.standard`)}>
                            <Nav.Item>
                                <Nav.Link eventKey={TAB_KEYS.STANDARD_BULK_EMAIL} disabled={isAnyTabLoading}>
                                    <FiMail size={15} aria-hidden />
                                    {t(`${PREFIX}.pageTabs.bulkEmail`)}
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey={TAB_KEYS.STANDARD_BULK_NOT_TRIGGERED_EMAIL} disabled={isAnyTabLoading}>
                                    <FiMail size={15} aria-hidden />
                                    {t(`${PREFIX}.pageTabs.bulkEmailNotTriggered`)}
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey={TAB_KEYS.STANDARD_BULK_SMS} disabled={isAnyTabLoading}>
                                    <FiMessageSquare size={15} aria-hidden />
                                    {t(`${PREFIX}.pageTabs.bulkSms`)}
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>
                        <Tab.Content>
                            <Tab.Pane eventKey={TAB_KEYS.STANDARD_BULK_EMAIL}>
                                {renderStandardNotifyPlaceholder('standardBulkEmail', t(`${PREFIX}.pageTabs.bulkEmail`))}
                            </Tab.Pane>
                            <Tab.Pane eventKey={TAB_KEYS.STANDARD_BULK_NOT_TRIGGERED_EMAIL}>
                                {renderStandardNotifyPlaceholder(
                                    'standardBulkNotTriggeredEmail',
                                    t(`${PREFIX}.pageTabs.bulkEmailNotTriggered`),
                                )}
                            </Tab.Pane>
                            <Tab.Pane eventKey={TAB_KEYS.STANDARD_BULK_SMS}>
                                {renderPlaceholderPanel(
                                    t(`${PREFIX}.pageTabs.bulkSms`),
                                    t(`${PREFIX}.standard.notifySoon`),
                                )}
                            </Tab.Pane>
                        </Tab.Content>
                    </Tab.Container>
                </Tab.Pane>
                <Tab.Pane eventKey={FAMILY_KEYS.SPECIFIC}>
                    <Tab.Container
                        activeKey={notifySubTab}
                        onSelect={(key) => {
                            if (key && !isAnyTabLoading) setNotifySubTab(key);
                        }}
                    >
                        <Nav className="fb-segmented fb-segmented--sub" role="tablist" aria-label={t(`${PREFIX}.pageTabs.specific`)}>
                            <Nav.Item>
                                <Nav.Link eventKey={TAB_KEYS.BULK_EMAIL} disabled={isAnyTabLoading}>
                                    <FiMail size={15} aria-hidden />
                                    {t(`${PREFIX}.pageTabs.bulkEmail`)}
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey={TAB_KEYS.BULK_SMS} disabled={isAnyTabLoading}>
                                    <FiMessageSquare size={15} aria-hidden />
                                    {t(`${PREFIX}.pageTabs.bulkSms`)}
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>
                        <Tab.Content>
                            <Tab.Pane eventKey={TAB_KEYS.BULK_EMAIL}>
                                {renderSpecificEmailForm()}
                                {renderResult()}
                            </Tab.Pane>
                            <Tab.Pane eventKey={TAB_KEYS.BULK_SMS}>
                                {renderPlaceholderPanel(
                                    t(`${PREFIX}.pageTabs.bulkSms`),
                                    t(`${PREFIX}.bulkSms.notifySoon`),
                                )}
                            </Tab.Pane>
                        </Tab.Content>
                    </Tab.Container>
                </Tab.Pane>
            </Tab.Content>
        </Tab.Container>
    );

    return (
        <div className="dashboard-page admin-trips-forecast-backup-check-page">
            <header className="fb-page-header">
                <h1 className="fb-page-title">
                    <FiEye size={28} aria-hidden />
                    {t(`${PREFIX}.title`)}
                </h1>
                <p className="fb-page-subtitle">{t(`${PREFIX}.subtitle`)}</p>
            </header>

            {current.loading
                && activeTab !== TAB_KEYS.STANDARD_IMPORT_EXCEL
                && !STANDARD_NOTIFY_PLACEHOLDER_KEYS.has(activeTab)
                && activeTab !== TAB_KEYS.BULK_SMS && (
                <div className="fb-global-progress">
                    {renderLoadingBanner()}
                </div>
            )}

            <Tab.Container
                activeKey={mainTab}
                onSelect={(key) => {
                    if (key && !isAnyTabLoading) setMainTab(key);
                }}
            >
                <Nav variant="tabs" className="fb-main-tabs" role="tablist">
                    <Nav.Item>
                        <Nav.Link eventKey={MAIN_TAB_KEYS.BACKUP_TOOLS} disabled={isAnyTabLoading}>
                            <FiEye size={16} aria-hidden />
                            {t(`${PREFIX}.pageTabs.backupTools`)}
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey={MAIN_TAB_KEYS.NOTIFICATION} disabled={isAnyTabLoading}>
                            <FiBell size={16} aria-hidden />
                            {t(`${PREFIX}.pageTabs.notification`)}
                        </Nav.Link>
                    </Nav.Item>
                </Nav>

                <Tab.Content>
                    <Tab.Pane eventKey={MAIN_TAB_KEYS.BACKUP_TOOLS}>
                        {renderBackupToolsContent()}
                    </Tab.Pane>
                    <Tab.Pane eventKey={MAIN_TAB_KEYS.NOTIFICATION}>
                        {renderNotificationContent()}
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>
        </div>
    );
};

const parseActionError = (requestError, tabKey, t) => {
    const status = requestError?.response?.status;
    const detail = requestError?.response?.data?.detail;
    const apiMessage = typeof detail === 'string'
        ? detail
        : (detail && typeof detail === 'object' && detail.message ? String(detail.message) : null);

    if (BACKUP_TAB_KEYS.has(tabKey)) {
        if (status === 400) return apiMessage || t(`${PREFIX}.standard.errors.badRequest`);
        if (status === 403) return apiMessage || t(`${PREFIX}.standard.errors.forbidden`);
        if (status === 404) return apiMessage || t(`${PREFIX}.standard.errors.notFound`);
    }
    if (tabKey === TAB_KEYS.BULK_EMAIL) {
        if (status === 403) return apiMessage || t(`${PREFIX}.bulkEmail.errors.forbidden`);
        if (status === 404) return apiMessage || t(`${PREFIX}.bulkEmail.errors.notFound`);
    }

    return formatRequestError(requestError, t(`${PREFIX}.error`), {
        timeout: t(`${PREFIX}.errors.timeout`),
        network: t(`${PREFIX}.errors.network`),
    });
};

export default AdminTripsObservationBackupCheck;
